import { Check, Loader2, X } from 'lucide-react';

import React from 'react';

import type { ImportChecklistState } from '@splitfiesta/core';

import { cn } from '@/lib/utils';

export interface ChecklistStep {
  key: string;
  label: string;
  state: ImportChecklistState;
}

const LABEL: Record<ImportChecklistState, string> = {
  done: 'text-gray-500 dark:text-gray-400',
  active: 'font-semibold text-gray-900 dark:text-white',
  pending: 'text-gray-400 dark:text-gray-500',
  failed: 'font-semibold text-error-600 dark:text-error-400',
};

function Glyph({
  state,
  index,
}: {
  state: ImportChecklistState;
  index: number;
}) {
  if (state === 'done') {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-success-500 text-white">
        <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-error-500 text-white">
        <X className="h-4 w-4" strokeWidth={3} aria-hidden />
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span className="flex h-7 w-7 items-center justify-center">
        <Loader2
          className="h-5 w-5 animate-spin text-primary-500 motion-reduce:animate-none"
          aria-hidden
        />
      </span>
    );
  }
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-gray-300 text-sm font-semibold text-gray-400 dark:border-gray-600 dark:text-gray-500">
      {index + 1}
    </span>
  );
}

interface Props {
  steps: ChecklistStep[];
  /** Each row is `${testId}-${step.key}`. */
  testId?: string;
}

/** The job's steps as a checklist: ticked, spinning, numbered, or crossed. */
export function StepChecklist({ steps, testId }: Props) {
  return (
    <ol className="space-y-1">
      {steps.map((step, index) => (
        <li
          key={step.key}
          data-testid={testId ? `${testId}-${step.key}` : undefined}
          data-state={step.state}
          className="flex items-center gap-3 py-1.5"
        >
          <Glyph state={step.state} index={index} />
          <span className={cn('text-base', LABEL[step.state])}>
            {step.label}
          </span>
        </li>
      ))}
    </ol>
  );
}
